// Regras da tarefa (P0.1): campos, limites, validação e atraso.
import { dueInstant, isValidDateKey, normalizeTime, type DateKey } from './dates';

export type TaskStatus = 'todo' | 'running' | 'paused' | 'done';

export interface Task {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  due_date: DateKey | null;
  due_time: string | null; // HH:MM
  estimate_minutes: number | null;
  status: TaskStatus;
  elapsed_seconds: number;
  running_since: string | null; // ISO
  completed_at: string | null; // ISO
  created_at: string; // ISO
  updated_at: string; // ISO
  /** posição arrastada, relativa à seção (0 = topo, 1 = fim); null = ordem automática (US-007) */
  position?: number | null;
  /** ocorrência de uma tarefa recorrente: o molde e o dia em que ela acontece (US-097) */
  recurring_id?: string | null;
  occurrence_date?: DateKey | null;
}

export const LIMITS = {
  title: 120,
  description: 500,
  estimateMin: 1,
  estimateMax: 720, // 12 h
} as const;

/** Atalhos de um clique: 15 min · 30 min · 1 h · 2 h. */
export const ESTIMATE_SHORTCUTS = [15, 30, 60, 120] as const;

export interface TaskInput {
  title: string;
  description?: string | null;
  due_date?: string | null;
  due_time?: string | null;
  estimate_minutes?: number | null;
}

export interface TaskFields {
  title: string;
  description: string | null;
  due_date: DateKey | null;
  due_time: string | null;
  estimate_minutes: number | null;
}

export type TaskField = keyof TaskFields;
export type TaskError =
  | 'title_required'
  | 'title_too_long'
  | 'description_too_long'
  | 'invalid_date'
  | 'invalid_time'
  | 'time_without_date'
  | 'estimate_out_of_range';

export type ValidationResult =
  | { ok: true; value: TaskFields }
  | { ok: false; errors: Partial<Record<TaskField, TaskError>> };

export function validateTaskInput(input: TaskInput): ValidationResult {
  const errors: Partial<Record<TaskField, TaskError>> = {};
  const title = (input.title ?? '').trim();
  if (!title) errors.title = 'title_required';
  else if ([...title].length > LIMITS.title) errors.title = 'title_too_long';

  const description = input.description?.trim() ? input.description.trim() : null;
  if (description && [...description].length > LIMITS.description) {
    errors.description = 'description_too_long';
  }

  const due_date = input.due_date || null;
  if (due_date && !isValidDateKey(due_date)) errors.due_date = 'invalid_date';

  let due_time: string | null = null;
  if (input.due_time) {
    due_time = normalizeTime(input.due_time);
    if (!due_time) errors.due_time = 'invalid_time';
    else if (!due_date) errors.due_time = 'time_without_date';
  }

  const est = input.estimate_minutes ?? null;
  if (est !== null && (!Number.isInteger(est) || est < LIMITS.estimateMin || est > LIMITS.estimateMax)) {
    errors.estimate_minutes = 'estimate_out_of_range';
  }

  if (Object.keys(errors).length) return { ok: false, errors };
  return {
    ok: true,
    value: { title, description, due_date, due_time, estimate_minutes: est },
  };
}

/** Converte "1:30", "90", "1h30", "2 h" em minutos (para "Outro" na estimativa). */
export function parseEstimate(text: string): number | null {
  const s = text.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;
  let m = /^(\d{1,2}):(\d{2})$/.exec(s);
  if (m) return Number(m[1]) * 60 + Number(m[2]);
  m = /^(\d{1,2})h(?:(\d{1,2})(?:min|m)?)?$/.exec(s);
  if (m) return Number(m[1]) * 60 + (m[2] ? Number(m[2]) : 0);
  m = /^(\d{1,3})(?:min|m)?$/.exec(s);
  if (m) return Number(m[1]);
  return null;
}

export function isOpen(task: Task): boolean {
  return task.status !== 'done';
}

/** Atrasada: tem prazo, o prazo já passou e não foi concluída. */
export function isOverdue(task: Task, now: Date): boolean {
  if (!task.due_date || task.status === 'done') return false;
  return dueInstant(task.due_date, task.due_time).getTime() < now.getTime();
}
